import { Field } from '@formily/core'
import { observer } from '@formily/reactive-vue'
import { isArr, isValid } from '@formily/shared'
import { h, useField } from '@formily/vue'
import { formatDate } from '@didi-pebble/pebble-design-vue/src/utils/date-util'
import { computed, defineComponent, Ref, toRef, ref } from 'vue-demi'
import type { CascaderProps } from '../cascader'
import type { DatePickerProps } from '../date-picker'
import { InputProps } from '../input'
import type { SelectProps } from '../select'
import { Space } from '../space'
import type { TimePickerProps } from '../time-picker'
import { stylePrefix } from '../__builtins__/configs'
import {
  composeExport,
  createContext,
  resolveComponent,
  useContext,
} from '../__builtins__/shared'

const prefixCls = `${stylePrefix}-preview-text`
const prebaseCls = `${stylePrefix}-base__preview`
const PlaceholderContext = createContext('N/A')

export const usePlaceholder = (value?: Ref<any>) => {
  const placeholderCtx = useContext(PlaceholderContext)
  const placeholder = computed(() => {
    return isValid(value?.value) && value.value !== ''
      ? value.value
      : resolveComponent(placeholderCtx.value) || 'N/A'
  })
  return placeholder
}

const Input = defineComponent<InputProps>({
  name: 'FPreviewTextInput',
  props: ['value'],
  setup(props, { attrs, slots }) {
    const value = toRef(props, 'value')
    const placeholder = usePlaceholder(value)
    return () => {
      return h(
        Space,
        {
          //input阅读态样式修改
          class: [prefixCls, prebaseCls],
          style: attrs.style,
        },
        {
          default: () => [
            slots?.prepend?.(),
            slots?.prefix?.(),
            placeholder.value,
            slots?.suffix?.(),
            slots?.append?.(),
          ],
        }
      )
    }
  },
})

const Select = observer(
  defineComponent<SelectProps>({
    name: 'FPreviewTextSelect',
    props: [],
    setup(_props, { attrs }) {
      const fieldRef = useField<Field>()
      const field = fieldRef.value
      const props = attrs as unknown as SelectProps
      const dataSource: any[] = field?.dataSource?.length
        ? field.dataSource
        : props?.options?.length
        ? props.options
        : []
      const placeholder = usePlaceholder()
      const getSelected = () => {
        const value = props.value
        if (props.multiple) {
          return isArr(value)
            ? value.map((val) => ({ label: val, value: val }))
            : []
        } else {
          return isValid(value) ? [{ label: value, value }] : []
        }
      }

      const getLabels = () => {
        const selected = getSelected()
        if (!selected.length) {
          return h(
            Space,
            {},
            {
              default: () => placeholder.value,
            }
          )
        }
        //select的阅读态
        let selectedtext = []
        for (let i = 0; i < selected.length; i++) {
          const text =
            dataSource?.find((item) => item.value == selected[i].value)
              ?.label || selected[i].label
          selectedtext.push(text)
        }
        return h(
          Space,
          {},
          {
            default: () => selectedtext.join(',') || placeholder.value,
          }
        )
      }

      return () => {
        return h(
          Space,
          {
            class: [prefixCls, prebaseCls],
            style: attrs.style,
          },
          {
            default: () => getLabels(),
          }
        )
      }
    },
  })
)

const Cascader = observer(
  defineComponent<CascaderProps>({
    name: 'FPreviewTextCascader',
    props: [],
    setup(_props, { attrs }) {
      const fieldRef = useField<Field>()
      const field = fieldRef.value
      const props = attrs as unknown as CascaderProps
      const dataSource: any[] = field?.dataSource?.length
        ? field.dataSource
        : props?.options?.length
        ? props.options
        : []
      const placeholder = usePlaceholder()
      const valueKey = props.props?.value || 'value'
      const labelKey = props.props?.label || 'label'
      const getSelected = () => {
        return isArr(props.value) ? props.value : []
      }

      const findLabel = (value: any, dataSource: any[]) => {
        for (let i = 0; i < dataSource?.length; i++) {
          const item = dataSource[i]
          if (item?.[valueKey] === value) {
            return item?.[labelKey]
          } else {
            const childLabel = findLabel(value, item?.children)
            if (childLabel) return childLabel
          }
        }
      }

      const getLabels = () => {
        const selected = getSelected()
        if (!selected?.length) {
          return h(
            Space,
            {},
            {
              default: () => placeholder.value,
            }
          )
        }
        //Cascader的阅读态
        let selectedtext = []
        for (let i = 0; i < selected.length; i++) {
          const text = findLabel(selected[i], dataSource)
          selectedtext.push(text)
        }
        return h(
          Space,
          {},
          {
            default: () => selectedtext.join('/') || placeholder.value,
          }
        )
      }
      return () => {
        return h(
          Space,
          {
            class: [prefixCls, prebaseCls],
            style: attrs.style,
          },
          {
            default: () => getLabels(),
          }
        )
      }
    },
  })
)

const DatePicker = defineComponent<DatePickerProps>({
  name: 'FPreviewTextDatePicker',
  props: [],
  setup(_props, { attrs }) {
    const props = attrs as unknown as DatePickerProps
    const placeholder = usePlaceholder()
    const getLabels = () => {
      if (isArr(props.value)) {
        const labels = (props.value as any[]).map(
          (value: String | Date) =>
            formatDate(value, props.format) || placeholder.value
        )

        return labels.join('~')
      } else {
        return formatDate(props.value, props.format) || placeholder.value
      }
    }

    return () => {
      return h(
        'div',
        {
          class: [prefixCls, prebaseCls],
          style: attrs.style,
        },
        {
          default: () => getLabels(),
        }
      )
    }
  },
})

const TimePicker = defineComponent<TimePickerProps>({
  name: 'FPreviewTextTimePicker',
  props: [],
  setup(_props, { attrs }) {
    const props = attrs as unknown as TimePickerProps
    const format = props.pickerOptions?.format || 'HH:mm:ss'
    const placeholder = usePlaceholder()
    const getLabels = () => {
      if (isArr(props.value)) {
        const labels = props.value.map(
          (value) => formatDate(value, format) || placeholder.value
        )

        return labels.join('~')
      } else {
        return formatDate(props.value, format) || placeholder.value
      }
    }

    return () => {
      return h(
        'div',
        {
          class: [prefixCls, prebaseCls],
          style: attrs.style,
        },
        {
          default: () => getLabels(),
        }
      )
    }
  },
})

const Text = defineComponent<any>({
  name: 'FPreviewText',
  setup(_props, { attrs }) {
    const placeholder = usePlaceholder()

    return () => {
      return h(
        'div',
        {
          class: [prefixCls, prebaseCls],
          style: attrs.style,
        },
        {
          default: () => placeholder.value,
        }
      )
    }
  },
})

// 新增Password的阅读态
const Password = observer(
  defineComponent<InputProps>({
    name: 'FPreviewTextPassword',
    props: ['value'],
    data() {
      return {
        isShowRealPassWord: false,
      }
    },
    setup(props, { attrs, slots }) {
      const value = toRef(props, 'value')
      const placeholder = usePlaceholder(value)
      let isShowRealPassWord = ref(false)
      return () => {
        const showReal = [
          placeholder.value,
          h(
            'i',
            {
              class: [`pb-input__icon`, 'gb-view'],
              on: {
                click: () => {
                  isShowRealPassWord.value = false
                },
              },
            },
            {}
          ),
        ]
        const showHidden = [
          '••••••',
          h(
            'i',
            {
              class: [`pb-input__icon`, 'gb-view-hide'],
              on: {
                click: () => {
                  isShowRealPassWord.value = true
                },
              },
            },
            {}
          ),
        ]
        return h(
          Space,
          {
            class: [prefixCls, prebaseCls],
            style: attrs.style,
          },
          {
            default: () => [isShowRealPassWord.value ? showReal : showHidden],
          }
        )
      }
    },
  })
)

export const PreviewText = composeExport(Text, {
  Input,
  Password,
  Select,
  Cascader,
  DatePicker,
  TimePicker,
  Placeholder: PlaceholderContext.Provider,
  usePlaceholder,
})

export default PreviewText
